import { toast } from "sonner"
import { Plugin } from "@tiptap/pm/state"
import { Decoration, DecorationSet } from "@tiptap/pm/view"
import { fileToBase64 } from "~/lib/file-upload"
import { getEditorContentFingerprint } from "~/lib/editor-fingerprint"
import { CustomEmoji } from "~/components/CustomEmoji"
import {
	BlockNoteSchema,
	defaultBlockSpecs,
	defaultInlineContentSpecs,
	defaultStyleSpecs,
	createStyleSpecFromTipTapMark,
	filterSuggestionItems,
	insertOrUpdateBlockForSlashMenu
} from "@blocknote/core"
import {
	withCollaboration,
	type CollaborationOptions
} from "@blocknote/core/yjs"
import { SuggestionMenu } from "@blocknote/core/extensions"
import { CommentsExtension } from "@blocknote/core/comments"
import {
	createReactInlineContentSpec,
	getDefaultReactSlashMenuItems,
	GridSuggestionMenuController,
	getDefaultReactEmojiPickerItems,
	BlockNoteViewEditor,
	FloatingComposerController,
	FloatingThreadController,
	FormattingToolbar,
	BlockTypeSelect,
	BasicTextStyleButton,
	CreateLinkButton,
	SuggestionMenuController,
	useCreateBlockNote,
	useExtensionState
} from "@blocknote/react"
import { BlockNoteView } from "@blocknote/shadcn"
import { blocknoteMenuComponents } from "./blocknote-menu"
import { AppWindow, Hash, Text } from "lucide-react"
import { usePostHog } from "posthog-js/react"
import { useNavigate } from "react-router-dom"
import { useWorkspace } from "~/components/workspace/Workspace"
import { useContext, useEffect, useMemo, useRef, type ReactNode } from "react"
import { TemplatePicker } from "./TemplatePicker"
import { Iframe } from "./Iframe"
import { FormEmbed } from "./FormEmbed"
import {
	QuestionBlock,
	FormPageBreak,
	FormFillContext,
	FormAnswerInline,
	QuestionSideMenuController,
	questionSlashItems
} from "~/components/forms/question-block"
import {
	renderCollaborationCursor,
	resolveCollaborationColor
} from "./CollaborationCursor"
import type { ContentTemplateKind } from "~/lib/enums"
import {
	mentionInlineContentConfig,
	customEmojiInlineContentConfig,
	parseBlockNoteContent,
	getBlockNotePlainText
} from "~/lib/editor-content"
import { EntityIdentity } from "~/components/EntityIdentity"
import { useTheme } from "~/components/providers/Theme"
import { useOrganization } from "~/hooks/useOrganization"
import { useTeam } from "~/hooks/useTeam"
import { api } from "~/trpc/react"
import {
	cn,
	normalizeIssueDescription,
	normalizeEditorContent
} from "~/lib/utils"
import {
	getMentionAutocompleteUsers,
	type MentionAutocompleteUser
} from "./plugins/MentionAutocomplete/utils"
import "@blocknote/shadcn/style.css"
import "./editor.css"

const editorSchema = BlockNoteSchema.create({
	styleSpecs: {
		...defaultStyleSpecs,
		// Imported Markdown/native content can combine code with links and
		// other formatting. Preserve those marks instead of rejecting the document.
		code: createStyleSpecFromTipTapMark(
			defaultStyleSpecs.code.implementation.mark.extend({ excludes: "" }),
			"boolean"
		)
	},
	blockSpecs: {
		...defaultBlockSpecs,
		iframe: Iframe(),
		formEmbed: FormEmbed(),
		question: QuestionBlock(),
		formPageBreak: FormPageBreak()
	},
	inlineContentSpecs: {
		...defaultInlineContentSpecs,
		formAnswer: FormAnswerInline,
		customEmoji: createReactInlineContentSpec(customEmojiInlineContentConfig, {
			render: ({ inlineContent }) => <CustomEmoji {...inlineContent.props} />,
			toExternalHTML: ({ inlineContent }) => (
				<span>:{inlineContent.props.name}:</span>
			)
		}),
		mention: createReactInlineContentSpec(mentionInlineContentConfig, {
			render: ({ inlineContent }) => (
				<EntityIdentity
					kind="user"
					userId={inlineContent.props.userId}
					name={inlineContent.props.name}
					variant="mention"
				/>
			),
			toExternalHTML: ({ inlineContent }) => (
				<span>@{inlineContent.props.name}</span>
			)
		})
	}
})

export function Editor({
	className,
	placeholder,
	compact = false,
	variant = "default",
	autoFocus = false,
	disabled = false,
	initialState,
	onChange,
	mentionUsers,
	teamId,
	ariaLabel,
	referencesEnabled = true,
	templateKind,
	collaboration,
	comments,
	children
}: {
	className?: string
	placeholder?: string
	compact?: boolean
	variant?: "default" | "composer" | "comment" | "issue" | "document" | "form"
	autoFocus?: boolean
	disabled?: boolean
	initialState: string | null
	onChange?: (value: string) => void
	mentionUsers?: MentionAutocompleteUser[]
	teamId?: string
	ariaLabel?: string
	referencesEnabled?: boolean
	templateKind?: ContentTemplateKind
	collaboration?: CollaborationOptions
	comments?:
		| Pick<
				Parameters<typeof CommentsExtension>[0],
				"threadStore" | "resolveUsers"
		  >
		| undefined
	children?: ReactNode
}) {
	const navigate = useNavigate()
	const workspace = useWorkspace()
	const fill = useContext(FormFillContext)
	const visiblePage = useRef<number | null>(null)
	visiblePage.current = fill?.filling ? (fill.pagination?.pageIndex ?? 0) : null
	const { colorScheme, preference } = useTheme()
	const { members, organization } = useOrganization()
	const { team } = useTeam()
	const customEmojis = api.emojis.list.useQuery(
		{ organizationId: organization?.id ?? "" },
		{
			enabled: !!organization && referencesEnabled && !disabled,
			staleTime: 30000,
			refetchInterval: 60000
		}
	)
	const referenceTeamId = teamId ?? team?.id
	const posthog = usePostHog()
	const upload = api.files.uploadTeamFile.useMutation()
	const uploadContext = useRef({
		teamId: referenceTeamId,
		disabled,
		referencesEnabled
	})
	uploadContext.current = {
		teamId: referenceTeamId,
		disabled,
		referencesEnabled
	}
	const blockNoteContent = parseBlockNoteContent(initialState)
	const emptyContent = normalizeIssueDescription(initialState) === null
	const baseEditorOptions = {
		schema: editorSchema,
		uploadFile: async (file: File) => {
			try {
				const context = uploadContext.current
				if (context.disabled || !context.referencesEnabled || !context.teamId)
					throw new Error(
						"Open an editable team document or issue to upload files"
					)
				if (file.size > 5 * 1024 * 1024)
					throw new Error("Files must be 5 MB or smaller")
				const result = await upload.mutateAsync({
					teamId: context.teamId,
					name: file.name,
					type: file.type,
					data: await fileToBase64(file)
				})
				posthog.capture("editor_file_uploaded", {
					teamId: context.teamId,
					type: file.type,
					size: file.size
				})
				return result.url
			} catch (error) {
				toast.error(
					error instanceof Error ? error.message : "File upload failed"
				)
				throw error
			}
		},
		placeholders: {
			default: placeholder ?? "Write something, or press '/' for commands"
		},
		domAttributes: {
			editor: {
				class: "editor-input",
				...(ariaLabel ? { "aria-label": ariaLabel } : {})
			}
		}
	}
	const editor = useCreateBlockNote(
		collaboration
			? withCollaboration({
					...baseEditorOptions,
					collaboration: {
						...collaboration,
						renderCursor:
							collaboration.renderCursor ?? renderCollaborationCursor
					},
					extensions: comments ? [CommentsExtension(comments)] : undefined
				})
			: {
					...baseEditorOptions,
					initialContent:
						blockNoteContent?.blocks ??
						(emptyContent ? [{ type: "paragraph" as const }] : undefined)
				},
		[collaboration?.fragment]
	)
	useEffect(() => {
		if (variant !== "form") return
		editor.registerExtension({
			key: "formPages",
			prosemirrorPlugins: [
				new Plugin({
					props: {
						decorations(state) {
							if (visiblePage.current === null) return DecorationSet.empty
							let page = 0
							const decorations: Decoration[] = []
							state.doc.descendants((node, position) => {
								if (node.type.name !== "blockContainer") return true
								const pageBreak = node.firstChild?.type.name === "formPageBreak"
								if (pageBreak || page !== visiblePage.current)
									decorations.push(
										Decoration.node(position, position + node.nodeSize, {
											style: "display: none",
											"aria-hidden": "true"
										})
									)
								if (pageBreak) page++
								return false
							})
							return DecorationSet.create(state.doc, decorations)
						}
					}
				})
			]
		})
		return () => {
			editor.unregisterExtension("formPages")
		}
	}, [editor, variant])
	useEffect(() => {
		if (variant !== "form") return
		const view = editor.prosemirrorView
		if (view) view.dispatch(view.state.tr.setMeta("formPages", true))
	}, [editor, variant, fill?.filling, fill?.pagination?.pageIndex])
	useEffect(() => {
		if (!collaboration) return
		collaboration.provider?.awareness?.setLocalStateField(
			"user",
			collaboration.user
		)
	}, [
		collaboration?.provider,
		collaboration?.user.name,
		collaboration?.user.color,
		collaboration?.user.colorToken
	])
	useEffect(() => {
		for (const cursor of editor.domElement?.querySelectorAll<HTMLElement>(
			".orbit-collaboration-cursor"
		) ?? []) {
			const { color, foreground } = resolveCollaborationColor(
				cursor.dataset.colorToken,
				cursor.dataset.color
			)
			cursor.style.setProperty("--collaborator-color", color)
			cursor.style.setProperty("--collaborator-foreground", foreground)
		}
	}, [editor, preference])
	const showEmojiSuggestions = useExtensionState(SuggestionMenu, {
		editor,
		selector: (state) =>
			state?.show === true &&
			state.triggerCharacter === ":" &&
			// Preserve explicit requests from the slash menu's Emoji command.
			(state.ignoreQueryLength === true || /^[\p{L}\p{N}]/u.test(state.query))
	})
	const issueReferenceQuery = useExtensionState(SuggestionMenu, {
		editor,
		selector: (state) => (state?.triggerCharacter === "#" ? state.query : "")
	})
	const { data: referencedIssue } = api.issues.resolveReference.useQuery(
		{
			organizationId: organization?.id ?? "",
			currentTeamId: referenceTeamId,
			reference: issueReferenceQuery
		},
		{
			enabled:
				!disabled &&
				referencesEnabled &&
				!!organization &&
				/^(?:[a-z0-9][a-z0-9-]*-)?[1-9]\d*$/i.test(issueReferenceQuery) &&
				(!!referenceTeamId || issueReferenceQuery.includes("-")),
			retry: false
		}
	)
	const issueReferenceItems = useMemo(
		() => async () => {
			const issue = referencedIssue
			if (!issue || !organization) return []
			return [false, true].map((showTitle) => ({
				title: `#${issue.key}${showTitle ? ` — ${issue.title}` : ""}`,
				subtext: showTitle ? "Issue ID & title" : "Just the issue ID",
				group: "How would you like this issue to be displayed?",
				icon: showTitle ? <Text size={18} /> : <Hash size={18} />,
				onItemClick: () => {
					editor.insertInlineContent([
						{
							type: "link",
							href: new URL(issue.url, window.location.origin).href,
							content: `#${issue.key}${showTitle ? ` — ${issue.title}` : ""}`
						},
						" "
					])
					posthog.capture("issue_reference_inserted", {
						presentation: showTitle ? "id_and_title" : "id",
						explicitTeam: issueReferenceQuery.includes("-"),
						organizationId: organization.id,
						teamId: referenceTeamId
					})
				}
			}))
		},
		[
			editor,
			organization,
			posthog,
			referenceTeamId,
			referencedIssue,
			issueReferenceQuery
		]
	)
	const lastValue = useRef(initialState)
	const replacing = useRef(false)

	useEffect(() => {
		// Props also change after server refreshes and draft resets. Do not replace
		// the document on our own onChange echoes (which would reset the cursor).
		if (collaboration) return
		if (
			initialState === lastValue.current &&
			(blockNoteContent || emptyContent)
		)
			return
		replacing.current = true
		lastValue.current = initialState
		let blocks = blockNoteContent?.blocks
		if (!blocks && !emptyContent) {
			blocks = parseBlockNoteContent(
				normalizeEditorContent(initialState)
			)?.blocks
		}
		editor.replaceBlocks(
			editor.document,
			blocks?.length ? blocks : [{ type: "paragraph" }]
		)
		replacing.current = false
	}, [editor, initialState])

	useEffect(() => {
		if (!collaboration || disabled) return
		const value = JSON.stringify({
			format: "blocknote",
			version: 1,
			blocks: editor.document,
			markdown: editor.blocksToMarkdownLossy()
		})
		lastValue.current = value
		if (
			getEditorContentFingerprint(value) !==
			getEditorContentFingerprint(initialState)
		)
			onChange?.(value)
	}, [editor])

	const { usersByName, mentionPattern } = useMemo(() => {
		const users = mentionUsers ?? members?.map((member) => member.user) ?? []
		const usersByName = new Map<string, (typeof users)[number] | null>()
		for (const user of users) {
			if (!user.name) continue
			const name = user.name.toLowerCase()
			usersByName.set(name, usersByName.has(name) ? null : user)
		}
		const names = [...usersByName.entries()]
			.filter(([, user]) => user)
			.map(([name]) => name)
			.sort((a, b) => b.length - a.length)
			.map((name) => name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
		return {
			usersByName,
			mentionPattern: names.length
				? new RegExp(`(^|\\s)@(${names.join("|")})(?=\\s|$|[.,!?;:])`, "gi")
				: undefined
		}
	}, [members, mentionUsers])
	const mentionPatternRef = useRef(mentionPattern)
	mentionPatternRef.current = mentionPattern

	useEffect(() => {
		if (disabled) return
		editor.registerExtension({
			key: "mentionBackspace",
			runsBefore: ["suggestionMenu"],
			keyboardShortcuts: {
				Backspace: ({ event }) => {
					const mentionPattern = mentionPatternRef.current
					const { empty, $from } = editor.prosemirrorState.selection
					if (event.isComposing || !empty || $from.parent.type.spec.code)
						return false
					const before = $from.nodeBefore
					if (before?.type.name === "mention") {
						editor.transact((tr) =>
							tr.delete($from.pos - before.nodeSize, $from.pos).scrollIntoView()
						)
						return true
					}
					if (
						!mentionPattern ||
						!before?.isText ||
						before.marks.some((mark) => mark.type.name === "code")
					)
						return false
					const textBefore = $from.parent.textBetween(
						0,
						$from.parentOffset,
						"\n",
						"\uFFFC"
					)
					const match = [...textBefore.matchAll(mentionPattern)].at(-1)
					if (!match || match.index + match[0].length !== textBefore.length)
						return false
					const textAfter = $from.parent.textBetween(
						$from.parentOffset,
						$from.parent.content.size,
						"\n",
						"\uFFFC"
					)
					if (textAfter && !/^[\s.,!?;:]/.test(textAfter)) return false
					editor.transact((tr) =>
						tr
							.delete($from.pos - match[0].length + match[1]!.length, $from.pos)
							.scrollIntoView()
					)
					return true
				}
			}
		})
		return () => {
			editor.unregisterExtension("mentionBackspace")
		}
	}, [disabled, editor])

	useEffect(() => {
		if (collaboration || !disabled || !mentionPattern) return
		// Enrich plain-text mentions for display without changing saved content.
		editor.forEachBlock((block) => {
			if (block.type === "codeBlock" || !Array.isArray(block.content))
				return true
			const content = block.content.slice(0, 0)
			let changed = false
			for (const part of block.content) {
				if (part.type !== "text" || part.styles.code) {
					content.push(part)
					continue
				}
				let cursor = 0
				for (const match of part.text.matchAll(mentionPattern)) {
					const user = usersByName.get(match[2]!.toLowerCase())
					if (!user) continue
					const start = match.index + match[1]!.length
					if (start > cursor)
						content.push({ ...part, text: part.text.slice(cursor, start) })
					content.push({
						type: "mention",
						props: { userId: user.id, name: user.name },
						content: undefined
					})
					cursor = match.index + match[0].length
					changed = true
				}
				if (cursor < part.text.length)
					content.push({ ...part, text: part.text.slice(cursor) })
			}
			if (changed) editor.updateBlock(block, { content })
			return true
		})
	}, [disabled, editor, initialState, mentionPattern, usersByName])

	useEffect(() => {
		if (autoFocus && !disabled) editor.focus()
	}, [autoFocus, disabled, editor])

	useEffect(() => {
		const element = editor.domElement
		if (disabled || !element) return
		const acceptMention = (event: KeyboardEvent) => {
			if (
				disabled ||
				event.key !== "Tab" ||
				event.shiftKey ||
				event.altKey ||
				event.ctrlKey ||
				event.metaKey ||
				event.isComposing
			)
				return
			const menu = editor.getExtension(SuggestionMenu)?.store.state
			const selectedId = element?.getAttribute("aria-activedescendant")
			const selectedItem = selectedId
				? element?.ownerDocument.getElementById(selectedId)
				: null
			if (
				!menu?.show ||
				!["@", "#"].includes(menu.triggerCharacter) ||
				!selectedItem
			)
				return
			event.preventDefault()
			event.stopPropagation()
			selectedItem.click()
		}
		element.addEventListener("keydown", acceptMention, true)
		return () => element.removeEventListener("keydown", acceptMention, true)
	}, [disabled, editor])

	return (
		<div
			onAuxClickCapture={(event) => {
				if (
					event.defaultPrevented ||
					event.button !== 1 ||
					!workspace ||
					workspace.isMobile
				)
					return
				const anchor =
					event.target instanceof Element
						? event.target.closest<HTMLAnchorElement>("a[href]")
						: null
				if (!anchor || anchor.hasAttribute("download")) return
				const url = new URL(anchor.href, window.location.href)
				if (
					url.origin !== window.location.origin ||
					!url.pathname.startsWith(`/${workspace.orgSlug}/`)
				)
					return
				event.preventDefault()
				event.stopPropagation()
				workspace.open(`${url.pathname}${url.search}${url.hash}`)
			}}
			onClickCapture={(event) => {
				if (
					event.defaultPrevented ||
					event.button !== 0 ||
					event.metaKey ||
					event.ctrlKey ||
					event.shiftKey ||
					event.altKey
				)
					return
				const anchor =
					event.target instanceof Element
						? event.target.closest<HTMLAnchorElement>("a[href]")
						: null
				if (!anchor || anchor.hasAttribute("download") || !organization) return
				const url = new URL(anchor.href, window.location.href)
				if (
					url.origin !== window.location.origin ||
					!url.pathname.startsWith(`/${organization.slug}/`)
				)
					return
				// BlockNote defaults to target=_blank and handles clicks itself.
				// Capture before its handler so references use the active Orbit pane.
				event.preventDefault()
				event.stopPropagation()
				void navigate(`${url.pathname}${url.search}${url.hash}`)
			}}
			data-variant={variant}
			data-readonly={disabled || undefined}
			className={cn(
				"orbit-editor relative w-full rounded-lg",
				(compact || variant === "composer") && "orbit-editor-compact",
				className
			)}
		>
			{variant !== "composer" &&
				!disabled &&
				templateKind &&
				referenceTeamId &&
				onChange && (
					<div className="absolute right-2 top-2 z-10">
						<TemplatePicker
							teamId={referenceTeamId}
							kind={templateKind}
							value={initialState}
							onApply={onChange}
						/>
					</div>
				)}
			<BlockNoteView
				className="orbit-editor-theme"
				renderEditor={false}
				emojiPicker={false}
				slashMenu={false}
				formattingToolbar={variant !== "composer"}
				editor={editor}
				theme={colorScheme}
				sideMenu={!compact && variant !== "composer" && variant !== "form"}
				editable={!disabled}
				shadCNComponents={blocknoteMenuComponents}
				onChange={() => {
					if (replacing.current || disabled) return
					const value = JSON.stringify({
						format: "blocknote",
						version: 1,
						blocks: editor.document,
						markdown: editor.blocksToMarkdownLossy()
					})
					lastValue.current = value
					onChange?.(value)
				}}
			>
				{variant === "composer" && !disabled && (
					<div className="orbit-composer-toolbar">
						<FormattingToolbar>
							<BlockTypeSelect />
							<BasicTextStyleButton basicTextStyle="bold" />
							<BasicTextStyleButton basicTextStyle="italic" />
							<BasicTextStyleButton basicTextStyle="underline" />
							<BasicTextStyleButton basicTextStyle="strike" />
							<BasicTextStyleButton basicTextStyle="code" />
							<CreateLinkButton />
						</FormattingToolbar>
						{templateKind && referenceTeamId && onChange && (
							<TemplatePicker
								teamId={referenceTeamId}
								kind={templateKind}
								value={initialState}
								onApply={onChange}
							/>
						)}
					</div>
				)}
				<BlockNoteViewEditor>
					{variant === "form" && !disabled && <QuestionSideMenuController />}
					{!disabled && (
						<SuggestionMenuController
							triggerCharacter="/"
							getItems={async (query) =>
								filterSuggestionItems(
									[
										...(variant === "form"
											? questionSlashItems.map((item) => ({
													title: item.label,
													aliases: [item.value, item.label.toLowerCase()],
													group: "Question",
													icon: <AppWindow size={18} />,
													onItemClick: () => {
														insertOrUpdateBlockForSlashMenu(editor, {
															type: "question",
															props: {
																qtype: item.value,
																choices:
																	item.value === "dropdown" ||
																	item.value === "radioGroup" ||
																	item.value === "checkboxGroup"
																		? "Option one\nOption two"
																		: ""
															}
														})
														posthog.capture("editor_form_question_inserted", {
															questionType: item.value,
															organizationId: organization?.id,
															teamId: referenceTeamId
														})
													}
												}))
											: []),
										...(variant === "form"
											? [
													{
														title: "Page break",
														aliases: ["page", "next"],
														group: "Form",
														icon: <AppWindow size={18} />,
														onItemClick: () =>
															insertOrUpdateBlockForSlashMenu(editor, {
																type: "formPageBreak",
																content: "Next page"
															})
													}
												]
											: []),
										...getDefaultReactSlashMenuItems(editor),
										{
											title: "Iframe",
											subtext: "Embed a web page",
											aliases: ["iframe", "embed", "website"],
											group: "Media",
											icon: <AppWindow size={18} />,
											onItemClick: () => {
												insertOrUpdateBlockForSlashMenu(editor, {
													type: "iframe"
												})
												posthog.capture("editor_iframe_inserted", {
													organizationId: organization?.id,
													teamId: referenceTeamId
												})
											}
										},
										...(variant === "document"
											? [
													{
														title: "Orbit form",
														subtext: "Embed one of this team's forms",
														aliases: ["form", "forms", "orbit form", "survey"],
														group: "Media",
														icon: <AppWindow size={18} />,
														onItemClick: () => {
															insertOrUpdateBlockForSlashMenu(editor, {
																type: "formEmbed"
															})
															posthog.capture("editor_form_embed_inserted", {
																organizationId: organization?.id,
																teamId: referenceTeamId
															})
														}
													}
												]
											: [])
									],
									query
								)
							}
						/>
					)}
					{!disabled && (
						<GridSuggestionMenuController
							triggerCharacter=":"
							minQueryLength={1}
							shouldOpen={(transaction) =>
								transaction.doc.textBetween(
									Math.max(0, transaction.selection.from - 1),
									transaction.selection.from
								) !== ":"
							}
							floatingUIOptions={{
								useFloatingOptions: {
									open: showEmojiSuggestions
								}
							}}
							columns={8}
							getItems={async (query) => [
								...(referencesEnabled ? (customEmojis.data ?? []) : [])
									.filter(
										(emoji) =>
											!emoji.archivedAt &&
											emoji.name.includes(query.toLowerCase())
									)
									.map((emoji) => ({
										id: `custom:${emoji.id}`,
										icon: (
											<img
												src={emoji.image}
												alt={`:${emoji.name}:`}
												title={`:${emoji.name}:`}
												className="size-5 object-contain"
											/>
										),
										onItemClick: () => {
											editor.insertInlineContent([
												{
													type: "customEmoji",
													props: {
														id: emoji.id,
														name: emoji.name,
														organizationId: organization?.id ?? ""
													}
												},
												" "
											])
											posthog.capture("custom_emoji_inserted", {
												emojiId: emoji.id,
												organizationId: organization?.id
											})
										}
									})),
								...(await getDefaultReactEmojiPickerItems(editor, query))
							]}
						/>
					)}

					{!disabled && referencesEnabled && organization && (
						<SuggestionMenuController
							triggerCharacter="#"
							key={issueReferenceQuery}
							// Only resolved references get a chooser. Incomplete/missing
							// references retain normal typing and Enter behavior.
							minQueryLength={referencedIssue ? 1 : Number.MAX_SAFE_INTEGER}
							shouldOpen={(tr) => {
								const { $from, empty } = tr.selection
								return (
									empty &&
									!$from.parent.type.spec.code &&
									!$from
										.marks()
										.some((mark) =>
											["code", "link"].includes(mark.type.name)
										) &&
									($from.parentOffset === 0 ||
										/\s$/.test($from.parent.textBetween(0, $from.parentOffset)))
								)
							}}
							getItems={issueReferenceItems}
						/>
					)}
					{!disabled && (mentionUsers || variant === "form") && (
						<SuggestionMenuController
							triggerCharacter="@"
							getItems={async (query) => {
								const earlierQuestions = []
								if (variant === "form") {
									const cursorId = editor.getTextCursorPosition().block.id
									const remaining = [...editor.document]
									while (remaining.length) {
										const block = remaining.shift()!
										if (block.id === cursorId) break
										if (block.type === "question") {
											const label =
												getBlockNotePlainText([{ ...block, children: [] }]) ||
												"Untitled question"
											earlierQuestions.push({
												title: label,
												group: "Answers",
												onItemClick: () =>
													editor.insertInlineContent([
														{
															type: "formAnswer",
															props: { fieldId: block.id, label }
														},
														" "
													])
											})
										}
										remaining.unshift(...block.children)
									}
								}
								return [
									...filterSuggestionItems(earlierQuestions, query),
									...getMentionAutocompleteUsers({
										users: mentionUsers ?? [],
										queryString: query
									}).map((user) => ({
										title: user.name,
										icon: (
											<EntityIdentity
												kind="user"
												variant="avatar"
												size="md"
												name={user?.name ?? "?"}
												image={user?.image}
												lastSeenAt={user?.lastSeenAt}
											/>
										),
										onItemClick: () =>
											editor.insertInlineContent([
												{
													type: "mention",
													props: { userId: user.id, name: user.name }
												},
												" "
											])
									}))
								]
							}}
						/>
					)}
				</BlockNoteViewEditor>
				{comments && (
					<>
						<FloatingComposerController />
						<FloatingThreadController />
					</>
				)}
				{children}
			</BlockNoteView>
		</div>
	)
}
